import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import Spinner from './Spinner';

/* Field System v2.0 — Button
   Mirrors the TX-6 button kit:
     - primary  → "record"  : white fill, black text, weight 700  (the one big CTA)
     - secondary → "shift"  : graphite surface, hairline border, hover steels up
     - outline   → "select" : transparent surface, hairline border, hover lifts to graphite
     - danger    → "peak"   : LED-orange outline + text, fills on hover.
                              Used as the alert/destructive variant. */

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-mono lowercase tracking-[0.08em] rounded-sm transition-all active:translate-y-px focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60",
  {
    variants: {
      variant: {
        default: "bg-foreground text-background border border-foreground font-bold hover:opacity-90",
        primary: "bg-foreground text-background border border-foreground font-bold hover:opacity-90",
        secondary: "bg-secondary text-foreground border border-border hover:border-muted-foreground",
        outline: "bg-transparent text-foreground border border-border hover:bg-secondary hover:border-muted-foreground",
        danger: "bg-transparent text-primary border border-primary hover:bg-primary hover:text-primary-foreground",
      },
      size: {
        sm: "px-3 py-1.5 text-xs min-h-[44px]",
        md: "px-5 py-2 text-xs min-h-[44px]",
        lg: "px-8 py-3 text-xs min-h-[44px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, Omit<VariantProps<typeof buttonVariants>, 'variant'> {
  variant?: ButtonVariant;
  loading?: boolean;
  children: React.ReactNode;
}

export default function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  children,
  className = '',
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <button
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={isDisabled}
      {...props}
    >
      {loading && <Spinner size="sm" className="mr-2" />}
      {children}
    </button>
  );
}
